import { NavLink, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  Smartphone,
  Users,
  UserSquare2,
  Wifi,
  Video,
  CalendarClock,
  Radio,
  Flame,
  MessageCircle,
  ScrollText,
  BarChart3,
  BellRing,
  Settings as SettingsIcon,
} from "lucide-react";
import { NAV } from "@/constants/testIds";
import { t, currentLang, setLang } from "@/lib/i18n";
import { api, auth } from "@/lib/api";
import { useEffect, useState } from "react";
import { LogOut } from "lucide-react";

const items = [
  { to: "/", label: t.nav.dashboard, icon: LayoutDashboard, tid: NAV.dashboard, end: true },
  { to: "/devices", label: t.nav.devices, icon: Smartphone, tid: NAV.devices },
  { to: "/accounts", label: t.nav.accounts, icon: Users, tid: NAV.accounts },
  { to: "/personas", label: t.nav.personas, icon: UserSquare2, tid: NAV.personas },
  { to: "/proxies", label: t.nav.proxies, icon: Wifi, tid: NAV.proxies },
  { to: "/videos", label: t.nav.videos, icon: Video, tid: NAV.videos },
  { to: "/schedule", label: t.nav.schedule, icon: CalendarClock, tid: NAV.schedule },
  { to: "/streams", label: t.nav.streams, icon: Radio, tid: NAV.streams },
  { to: "/warmup", label: t.nav.warmup, icon: Flame, tid: NAV.warmup },
  { to: "/comments", label: t.nav.comments, icon: MessageCircle, tid: NAV.comments },
  { to: "/logs", label: t.nav.logs, icon: ScrollText, tid: NAV.logs },
  { to: "/analytics", label: t.nav.analytics, icon: BarChart3, tid: NAV.analytics },
  { to: "/alerts", label: t.nav.alerts, icon: BellRing, tid: NAV.alerts },
  { to: "/settings", label: t.nav.settings, icon: SettingsIcon, tid: NAV.settings },
];

export default function Layout() {
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const s = await api.summary();
        alive && setAlertCount(s?.counts?.alerts_unack ?? 0);
      } catch { /* ignore */ }
    };
    load();
    const tm = setInterval(load, 20000);
    return () => {
      alive = false;
      clearInterval(tm);
    };
  }, []);

  return (
    <div className="min-h-screen flex">
      <aside className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-200">
          <div className="h-9 w-9 rounded-xl bg-emerald-500 flex items-center justify-center text-white font-display font-bold shadow-md shadow-emerald-500/30">
            {currentLang === "en" ? "T" : "Т"}
          </div>
          <div className="leading-tight flex-1">
            <div className="font-display font-semibold text-slate-900">{t.nav.brand}</div>
            <div className="text-[11px] text-slate-500 uppercase tracking-widest">
              {t.nav.brandTag}
            </div>
          </div>
          <div className="flex items-center gap-0.5 rounded-lg bg-slate-100 p-0.5" data-testid="lang-switcher">
            <button
              data-testid="lang-ru"
              onClick={() => currentLang !== "ru" && setLang("ru")}
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${
                currentLang === "ru" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              RU
            </button>
            <button
              data-testid="lang-en"
              onClick={() => currentLang !== "en" && setLang("en")}
              className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md transition-colors ${
                currentLang === "en" ? "bg-white text-emerald-600 shadow-sm" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              EN
            </button>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {items.map(({ to, label, icon: Icon, tid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {label === t.nav.alerts && alertCount > 0 && (
                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600">
                  {alertCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <button
            data-testid="logout-btn"
            onClick={() => { auth.clear(); window.location.href = "/login"; }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-rose-600 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>{t.nav.logout || "Выйти"}</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
